import {
  type ColorRepresentation,
  BufferGeometry,
  Color,
  MeshBasicMaterial,
  MeshLambertMaterial,
  Object3D,
  Vector3,
} from 'three';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

import { usePreloadStore } from 'state/preloadStore';
import type { PrimitiveProps } from 'types/r3f';

export interface TileProps extends Omit<PrimitiveProps, 'object' | 'position'> {
  uid: string;
  url: string;
  offset: Vector3;
  tint?: ColorRepresentation;
}

const WHITE_COLOR = new Color(0xffffff);

class Tile extends Object3D {
  copyright: string | null = null;

  constructor(props: TileProps) {
    super();

    this.userData = { ...props.userData, uid: props.uid };

    // Use a preloaded glTF
    const model = this.getModel(props.url);

    // Replace the default MeshBasicMaterial with a MeshLambertMaterial.
    // MeshLambertMaterial allows for more interactions
    // with the environment and effects, i.e. directional light source.
    this.applyLambertMaterial(model, props.tint);

    this.add(model.scene);

    // Geometry inside the glTF scene is not positioned at (0, 0, 0),
    // so we need to apply an offset.
    this.position.copy(props.offset);

    this.copyright = model.asset.copyright ?? null;
  }

  private getModel(url: string): GLTF {
    const preloadedModels = usePreloadStore.getState().glTFs;
    return preloadedModels[url];
  }

  private applyLambertMaterial(model: GLTF, tint?: ColorRepresentation) {
    model.scene.traverse(obj => {
      if ('material' in obj && obj.material instanceof MeshBasicMaterial) {
        obj.material = new MeshLambertMaterial({
          map: obj.material.map,
          color: tint ?? WHITE_COLOR,
        });
      }
      // Needed for the MeshLambertMaterial to work properly
      if ('geometry' in obj && obj.geometry instanceof BufferGeometry) {
        obj.geometry.computeVertexNormals();
      }
    });
  }
}

export default Tile;
